import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppNav from './components/layout/AppNav';
import Footer from './components/layout/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import SkipLink from './components/layout/SkipLink';
import HomePage from './pages/HomePage';
import ShulsPage from './pages/ShulsPage';
import ShulPage from './pages/ShulPage';
import MinyanSearchPage from './pages/MinyanSearchPage';
import CalendarPage from './pages/CalendarPage';
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/AdminPage';
import SuperAdminPage from './pages/SuperAdminPage';
import JoinPage from './pages/JoinPage';
import DirectoryPage from './pages/DirectoryPage';
import DirectoryListingPage from './pages/DirectoryListingPage';
import AddListingPage from './pages/AddListingPage';
import ContactPage from './pages/ContactPage';
import LegalPage from './pages/LegalPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import NotFoundPage from './pages/NotFoundPage';
import { EN_PREFIX, langFromPath } from './utils/langPath';

function AppContent() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const lang = i18n.language;
    document.documentElement.dir = lang === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [i18n.language]);

  // Back / forward to an address in the other copy: follow its language.
  useEffect(() => {
    const onPop = () => {
      const lang = langFromPath(window.location.pathname);
      if (lang !== i18n.language) i18n.changeLanguage(lang);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [i18n]);

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <SkipLink />
      <AppNav />
      <main id="main" tabIndex={-1} className="flex-1 pt-[calc(var(--nav-bottom)-0.75rem)] focus:outline-none">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/shuls" element={<ShulsPage />} />
          <Route path="/minyanim" element={<MinyanSearchPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/shul/:shulId" element={<ShulPage />} />
          <Route path="/directory" element={<DirectoryPage />} />
          <Route path="/directory/add" element={<AddListingPage />} />
          <Route path="/directory/:slug" element={<DirectoryPage />} />
          <Route path="/directory/:slug/:listingId" element={<DirectoryListingPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<LegalPage kind="privacy" />} />
          <Route path="/terms" element={<LegalPage kind="terms" />} />
          <Route path="/accessibility" element={<LegalPage kind="accessibility" />} />
          <Route path="/reset" element={<ResetPasswordPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['editor', 'superAdmin']}>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super"
            element={
              <ProtectedRoute allowedRoles={['superAdmin']}>
                <SuperAdminPage />
              </ProtectedRoute>
            }
          />
          <Route path="/join/:token" element={<JoinPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  const { i18n } = useTranslation();
  // English pages live under /en (docs/seo-plan.md): every link inside the app
  // gets the prefix by itself. A new language = a new router (key).
  const isEnglish = i18n.language === 'en';
  return (
    <BrowserRouter key={i18n.language} basename={isEnglish ? EN_PREFIX : undefined}>
      <AppContent />
    </BrowserRouter>
  );
}
